import { InvalidHeroContentError } from '../errors/InvalidHeroContentError';

const MAX_HEADLINE_LENGTH = 70; // keeps the H1 usable as an SEO-friendly <title> fallback
const MAX_SUBHEADLINE_LENGTH = 160; // aligns with meta-description-length conventions

export interface HeroContentInput {
  headline: string;
  subheadline: string;
  ctaLabel: string;
  ctaHref: string;
}

/**
 * Value Object per docs/architecture/ARCHITECTURE-V2-ADDENDUM.md (TASK 1).
 * Immutable and self-validating: it is impossible to hold an instance of
 * HeroContent that violates the business rules below.
 */
export class HeroContent {
  public readonly headline: string;
  public readonly subheadline: string;
  public readonly ctaLabel: string;
  public readonly ctaHref: string;

  private constructor(input: HeroContentInput) {
    this.headline = input.headline;
    this.subheadline = input.subheadline;
    this.ctaLabel = input.ctaLabel;
    this.ctaHref = input.ctaHref;
    Object.freeze(this);
  }

  public static create(input: HeroContentInput): HeroContent {
    const headline = input.headline.trim();
    const subheadline = input.subheadline.trim();
    const ctaLabel = input.ctaLabel.trim();
    const ctaHref = input.ctaHref.trim();

    if (headline.length === 0) {
      throw new InvalidHeroContentError('headline must not be empty.');
    }
    if (headline.length > MAX_HEADLINE_LENGTH) {
      throw new InvalidHeroContentError(
        `headline must be at most ${MAX_HEADLINE_LENGTH} characters (got ${headline.length}).`
      );
    }
    if (subheadline.length > MAX_SUBHEADLINE_LENGTH) {
      throw new InvalidHeroContentError(
        `subheadline must be at most ${MAX_SUBHEADLINE_LENGTH} characters (got ${subheadline.length}).`
      );
    }
    if (ctaLabel.length === 0) {
      throw new InvalidHeroContentError('ctaLabel must not be empty.');
    }
    if (!ctaHref.startsWith('/') && !/^https?:\/\//.test(ctaHref)) {
      throw new InvalidHeroContentError(
        'ctaHref must be an internal path starting with "/" or an absolute http(s) URL.'
      );
    }

    return new HeroContent({ headline, subheadline, ctaLabel, ctaHref });
  }
}
